import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { GameState, AppState } from '../types/game';
import { useGameState } from './useGameState';
import { useGameTimer } from './useGameTimer';
import { usePrizeAnimation } from './usePrizeAnimation';
import { Product, extractProductId } from './useProducts';
import { ApiManager } from '../services/ApiManager';
import { useAppDispatch } from '../store/hooks';
import {
  setClaimedPrizes as setReduxClaimedPrizes,
  setWalletAmount as setReduxWalletAmount,
  setClaimedPrizeIds as setReduxClaimedPrizeIds,
  setItems as setReduxItems,
} from '../store/slices/gameSlice';
import { distributeItemsEvenly } from '../utils/GameUtils';
import { getPrizeAmount } from '../constants/gameConfig';
import { useSettingsRedux } from './useSettingsRedux';

export const useGame = ({
  fetchedProducts,
  loadingProducts,
  productFetchError,
  timeLimitInSeconds,
  onTimerComplete,
}: {
  fetchedProducts: Product[];
  loadingProducts: boolean;
  productFetchError: Error | null;
  timeLimitInSeconds: number | undefined;
  onTimerComplete?: () => void;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const rootPath = process.env.REACT_APP_ROOT_PATH || '';
  const { settings } = useSettingsRedux();

  const {
    items,
    setItems,
    loading,
    setLoading,
    claimedPrizes,
    setClaimedPrizes,
    walletAmount,
    setWalletAmount,
    claimedPrizeIds,
    setClaimedPrizeIds,
    appState,
    setAppState,
    selectedProduct,
    setSelectedProduct,
    setGameState,
  } = useGameState(fetchedProducts, loadingProducts, settings);

  const { timeRemaining, setTimeRemaining, resetTimer, initializeTimer } = useGameTimer(
    timeLimitInSeconds || 0,
    onTimerComplete
  );

  const {
    showPrizeAnimation,
    showDiscountApplied,
    flyingPrizes,
    flyingDiscounts,
    walletPulse,
    discountPulse,
    walletRef,
    triggerPrizeAnimation,
    triggerDiscountAnimation,
    triggerWalletToDiscountAnimation,
  } = usePrizeAnimation();

  const handlePrizeClick = useCallback(
    async (prizeId: string, event: React.MouseEvent<HTMLElement>) => {
      const prizeIndex = items.findIndex((item) => item.type === 'prize' && item.item.id === prizeId);
      if (prizeIndex === -1) return;

      const currentItem = items[prizeIndex];
      if (currentItem.type !== 'prize' || currentItem.item.claimed) return;

      const prizeAmount = getPrizeAmount(settings);
      const newClaimedPrizes = claimedPrizes + 1;
      const newWalletAmount = walletAmount + prizeAmount;
      const newClaimedPrizeIds = [...claimedPrizeIds, prizeId];

      const newItems = [...items];
      newItems[prizeIndex] = {
        ...currentItem,
        item: { ...currentItem.item, claimed: true },
      };

      setItems(newItems);
      setClaimedPrizes(newClaimedPrizes);
      setWalletAmount(newWalletAmount);
      setClaimedPrizeIds(newClaimedPrizeIds);

      dispatch(setReduxItems(newItems));
      dispatch(setReduxClaimedPrizes(newClaimedPrizes));
      dispatch(setReduxWalletAmount(newWalletAmount));
      dispatch(setReduxClaimedPrizeIds(newClaimedPrizeIds));

      const updatedGameState: GameState = {
        claimedPrizes: newClaimedPrizes,
        walletAmount: newWalletAmount,
        claimedPrizeIds: newClaimedPrizeIds,
        selectedProductId: selectedProduct?.id,
        appState,
        distributedItems: newItems.map((item) => ({
          type: item.type,
          id: item.item.id,
          claimed: item.type === 'prize' ? item.item.claimed : undefined,
        })),
      };

      setGameState(updatedGameState);

      window.dispatchEvent(
        new CustomEvent('gameStateUpdated', {
          detail: {
            claimedPrizes: newClaimedPrizes,
            walletAmount: newWalletAmount,
            claimedPrizeIds: newClaimedPrizeIds,
          },
        })
      );

      triggerPrizeAnimation(prizeId, currentItem.item.imageUrl, event.clientX, event.clientY);

      try {
        await ApiManager.analytics.playmerce_collect({
          count: newClaimedPrizes,
        });
      } catch (error) {
        console.error('Failed to send analytics event:', error);
      }
    },
    [
      items,
      setItems,
      claimedPrizes,
      walletAmount,
      claimedPrizeIds,
      appState,
      setClaimedPrizes,
      setWalletAmount,
      setClaimedPrizeIds,
      triggerPrizeAnimation,
      selectedProduct,
      setGameState,
      settings,
    ]
  );

  const handleProductClick = useCallback(
    (product: Product) => {
      if (!product || !product.id) {
        console.warn('Invalid product or product ID');
        return;
      }

      setSelectedProduct(product);

      const numericId = extractProductId(product.id);
      if (!numericId) {
        console.warn('Could not extract numeric ID from product:', product.id);
        return;
      }

      navigate(`${rootPath}/product/${numericId}`);

      triggerDiscountAnimation();
    },
    [navigate, rootPath, setSelectedProduct, triggerDiscountAnimation]
  );

  return {
    items,
    loading,
    claimedPrizes,
    walletAmount,
    claimedPrizeIds,
    timeRemaining,
    selectedProduct,
    appState,
    setAppState,

    showPrizeAnimation,
    showDiscountApplied,
    flyingPrizes,
    flyingDiscounts,
    walletPulse,
    discountPulse,
    walletRef,

    handlePrizeClick,
    handleProductClick,
    initializeTimer,
  };
};
